import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, MessageSquare, Grid3x3 } from "lucide-react";
import { useUserAuth } from "../context/UserAuthContext";
import { Seo } from "../components/Seo";

export default function Account() {
  const { user, loading, signOut, signIn } = useUserAuth();
  const navigate = useNavigate();

  if (loading)
    return <div className="min-h-screen flex items-center justify-center text-stone">Loading…</div>;

  if (!user)
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand px-6" data-testid="account-signed-out">
        <Seo title="Sign In" path="/account" noIndex />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-md w-full text-center"
        >
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6">Client Portal</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[1] mb-6">Sign in to your BlueGreen account</h1>
          <p className="text-stone text-base leading-relaxed mb-10">
            Sign in with Google to save your design quiz responses, revisit your brief and stay in touch with the studio.
          </p>
          <button
            data-testid="account-signin-button"
            onClick={signIn}
            className="w-full bg-forest text-sand py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-teal transition-colors duration-500"
          >
            Continue with Google
          </button>
        </motion.div>
      </div>
    );

  return (
    <div className="min-h-screen bg-sand-dark/40 pt-32 pb-24 px-6 md:px-12" data-testid="account-page">
      <Seo title={`${user.name || "Account"} · Client Portal`} path="/account" noIndex />
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="bg-sand border border-ink/10 p-8 md:p-12"
        >
          <div className="flex items-center justify-between gap-6 mb-10">
            <div className="flex items-center gap-5">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover border border-ink/10" data-testid="user-avatar" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-forest text-sand flex items-center justify-center font-serif text-2xl">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-teal mb-1">Signed in</p>
                <h1 className="font-serif text-3xl md:text-4xl text-ink leading-tight">Welcome, {user.name || user.email.split("@")[0]}</h1>
                <p className="text-sm text-stone mt-1">{user.email}</p>
              </div>
            </div>
            <button
              data-testid="account-signout-button"
              onClick={async () => { await signOut(); navigate("/"); }}
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-stone hover:text-teal transition-colors"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <Link to="/quiz" data-testid="account-quiz-link" className="group border border-ink/10 p-6 md:p-8 hover:bg-sand-dark/40 transition-colors">
              <MessageSquare size={22} className="text-forest group-hover:text-teal transition-colors mb-4" />
              <h2 className="font-serif text-2xl text-ink group-hover:text-teal transition-colors">Take the Quiz</h2>
              <p className="text-stone text-sm mt-2 leading-relaxed">Share your dream home in two minutes and we'll respond with a considered brief.</p>
            </Link>
            <Link to="/#work" data-testid="account-work-link" className="group border border-ink/10 p-6 md:p-8 hover:bg-sand-dark/40 transition-colors">
              <Grid3x3 size={22} className="text-forest group-hover:text-teal transition-colors mb-4" />
              <h2 className="font-serif text-2xl text-ink group-hover:text-teal transition-colors">Browse Recent Work</h2>
              <p className="text-stone text-sm mt-2 leading-relaxed">Explore our latest residences across Bangalore and take notes for your own brief.</p>
            </Link>
          </div>

          <button
            data-testid="account-signout-button-mobile"
            onClick={async () => { await signOut(); navigate("/"); }}
            className="md:hidden mt-8 w-full border border-ink/15 py-4 text-xs uppercase tracking-[0.2em] font-bold text-stone hover:text-teal transition-colors"
          >
            Sign out
          </button>
        </motion.div>
      </div>
    </div>
  );
}
